import React, { useEffect, useState, useMemo } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';

const toHHMM = (v) => {
  if (!v) return '';
  const s = String(v);
  const m = s.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return s;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

const getEventStatus = (startTime, endTime, dayIso) => {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  if (dayIso !== today) {
    return dayIso < today ? 'past_day' : 'future_day';
  }

  const nowHHMM = now.toTimeString().slice(0, 5);
  const s = toHHMM(startTime);
  const e = toHHMM(endTime);
  if (nowHHMM >= s && nowHHMM < e) return 'ongoing';
  if (nowHHMM > e) return 'past';
  return 'upcoming';
};

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/planning', setUserInfo, setLoading, setError, []);
  }, []);

  useEffect(() => {
    const any = userInfo?.planning_by_day && Object.values(userInfo.planning_by_day).flat();
    if (!loading && any?.length > 0) {
      const ongoing = document.querySelector('.plan-item--ongoing');
      if (ongoing) ongoing.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, userInfo]);

  const planningByDay = userInfo?.planning_by_day || {};
  const dayEntries = Object.entries(planningByDay);

  const todayIso = useMemo(() => new Date().toISOString().split('T')[0], []);

  return (
    <Base activeNav="home">
      <Card className="schedule-header" mb="2">
        <Back to="/home" />
        <Title>
          <Icon name="calendar-lines" className="me-2" theme="primary" style={{ fontSize: '1.5rem' }} />
          {t('planning')}
        </Title>
      </Card>

      {dayEntries.length > 0 ? (
        <div className="schedule">
          {dayEntries.map(([dayIso, items]) => {
            const isUnscheduled = dayIso === '1900-01-01';
            const isToday = dayIso === todayIso;
            const isPast = !isUnscheduled && dayIso < todayIso;

            const date = !isUnscheduled ? new Date(dayIso) : null;
            const dayLabel = isUnscheduled
              ? (t('unscheduled') || 'Unscheduled / TBC')
              : date.toLocaleDateString(undefined, { weekday: 'long' });
            const dateLabel = isUnscheduled
              ? ''
              : date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });

            return (
              <div
                key={dayIso}
                className={`schedule-day ${isToday ? 'schedule-day--today' : ''} ${
                  isPast ? 'schedule-day--past' : ''
                }`}
              >
                <div className="schedule-day__header mx-3">
                  <div className="schedule-day__heading">
                    <Icon name="calendar" className="me-2" theme={isToday ? 'primary' : 'secondary'} />
                    <span className="schedule-day__weekday">{dayLabel}</span>
                    {dateLabel && <span className="schedule-day__date">{dateLabel}</span>}
                  </div>
                  {isToday && <span className="schedule-day__today-pill">{t('today')}</span>}
                </div>

                <div className="schedule-day__items">
                  {items.map((activity, idx) => {
                    const startHHMM = toHHMM(activity.start_time);
                    const endHHMM = toHHMM(activity.end_time);
                    const status = getEventStatus(activity.start_time, activity.end_time, dayIso);
                    const isOngoing = status === 'ongoing';
                    const isPastItem = status === 'past' || status === 'past_day';

                    return (
                      <Card
                        key={`${dayIso}-${activity.id}-${idx}`}
                        p="3"
                        mx="3"
                        mb="1"
                        className={`plan-item ${isOngoing ? 'plan-item--ongoing' : ''} ${
                          isPastItem ? 'plan-item--past' : ''
                        } ${activity.is_challenge ? 'plan-item--challenge' : ''}`}
                        to={activity.is_challenge ? '/score' : undefined}
                        onClick={
                          activity.is_challenge
                            ? () => sessionStorage.setItem('selectedScoreId', activity.id)
                            : undefined
                        }
                      >
                        <div className="plan-item__row">
                          <div className="plan-item__time">
                            <div className="plan-item__time-start">{startHHMM}</div>
                            {endHHMM && <div className="plan-item__time-end">{endHHMM}</div>}
                          </div>

                          <div className="plan-item__divider" aria-hidden="true">
                            <span className="plan-item__dot" />
                          </div>

                          <div className="plan-item__body">
                            <div className="plan-item__name">{activity.name}</div>
                            <div className="plan-item__meta">
                              {isOngoing && (
                                <span className="plan-item__status plan-item__status--live">
                                  <span className="plan-item__live-dot" /> {t('live')}
                                </span>
                              )}
                              {!isOngoing && isPastItem && (
                                <span className="plan-item__status plan-item__status--past">
                                  <Icon name="check" /> {t('done')}
                                </span>
                              )}
                              {!isOngoing && !isPastItem && (
                                <span className="plan-item__status plan-item__status--upcoming">
                                  <Icon name="clock" /> {t('upcoming')}
                                </span>
                              )}
                            </div>
                          </div>

                          {activity.is_challenge && (
                            <Icon name="angle-right" className="plan-item__chevron" />
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <NotFound translationKey="noScheduleFound" loading={loading} />
      )}
    </Base>
  );
};

export default Page;
